/**
 * HashMedora Player API
 * Client-side placeholder for player data.
 * Uses localStorage. Replace with backend calls when ready.
 */

import { BigNumber, ethers } from 'ethers';
import { storage } from './storage';
import { GAMES } from '../data/games';
import { coreVaultAddress } from '../utils/addressHelpers.ts';
import {
  ETHERSCAN_API_KEY,
  ETHERSCAN_BSC_MAINNET_CHAIN_ID,
  ETHERSCAN_V2_API_BASE,
  VAULT_WINNER_EVENT_TOPIC0,
  ADD_BOUGHT_DATA_API_URL,
} from '../utils/constants.ts';
import {
  decodeVaultWinnerLog,
  fetchBoughtDataFromBackend,
  fetchVaultWinnerLogsAllPages,
  fetchWinnerDataFromBackend,
  mergeBoughtRowsByTxHash,
} from './analyticsApi.js';
import {
  hasDateRangeFilter,
  parseFilterTimestampFrom,
  parseFilterTimestampTo,
} from '../utils/constants.ts';

export { VAULT_WINNER_EVENT_TOPIC0 } from '../utils/constants.ts';
export { decodeVaultWinnerLog, fetchVaultWinnerLogsAllPages };

/** Notify backend after a successful on-chain ticket purchase. */
export async function addBoughtData({ txHash, timestamp, numbersArg, betGameTypeBytes32, address, roundId }) {
  const res = await fetch(ADD_BOUGHT_DATA_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      txHash: String(txHash),
      timestamp,
      numbersArg,
      betGameTypeBytes32: String(betGameTypeBytes32),
      address: address,
      roundId : roundId
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `addBoughtData failed: ${res.status}`);
  }
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/**
 * Decode vault payout rows from one transaction receipt (BSC Mainnet via Etherscan
 * `eth_getTransactionReceipt`). Filters logs by `coreVaultAddress` and `VAULT_WINNER_EVENT_TOPIC0`.
 * The largest reward row is treated as the primary winner; remaining rows are neighbors.
 */
export async function fetchVaultWinnerPayoutsFromTxHash(txHash) {
  const raw = String(txHash || '').trim();
  if (!raw) return { rows: [], primary: null, neighbors: [], txHashQueried: '', error: 'missing_tx' };
  const hash = raw.startsWith('0x') ? raw : `0x${raw}`;
  const url = `${ETHERSCAN_V2_API_BASE}?chainid=${ETHERSCAN_BSC_MAINNET_CHAIN_ID}&module=proxy&action=eth_getTransactionReceipt&txhash=${encodeURIComponent(hash)}&apikey=${ETHERSCAN_API_KEY}`;
  let json;
  try {
    const res = await fetch(url, { method: 'GET' });
    json = await res.json();
  } catch (e) {
    return { rows: [], primary: null, neighbors: [], txHashQueried: hash, error: e?.message || 'network_error' };
  }
  const receipt = json?.result;
  if (!receipt || typeof receipt === 'string') {
    return {
      rows: [],
      primary: null,
      neighbors: [],
      txHashQueried: hash,
      error: typeof receipt === 'string' ? receipt : json?.message || 'receipt_unavailable',
    };
  }
  const vault = coreVaultAddress.toLowerCase();
  const t0 = VAULT_WINNER_EVENT_TOPIC0.toLowerCase();
  const logs = Array.isArray(receipt.logs) ? receipt.logs : [];
  const rows = [];
  for (const l of logs) {
    const addr = String(l.address || '').toLowerCase();
    const topics = Array.isArray(l.topics) ? l.topics : [];
    if (addr !== vault || String(topics[0] || '').toLowerCase() !== t0) continue;
    const row = decodeVaultWinnerLog({
      topics,
      data: l.data,
      transactionHash: l.transactionHash || hash,
      timeStamp: receipt.timeStamp,
      blockNumber: l.blockNumber ?? receipt.blockNumber,
      logIndex: l.logIndex,
    });
    if (row) rows.push(row);
  }
  rows.sort((a, b) => {
    const da = BigNumber.from(a.rewardWeiStr || '0');
    const db = BigNumber.from(b.rewardWeiStr || '0');
    if (db.gt(da)) return 1;
    if (db.lt(da)) return -1;
    return 0;
  });
  const primary = rows[0] ?? null;
  const neighbors = rows.slice(1);
  return { rows, primary, neighbors, txHashQueried: hash, error: null };
}

/** @deprecated Use `fetchVaultWinnerLogsAllPages` from `analyticsApi`. */
export async function fetchWinNearLogsAllPages(maxPages = 50) {
  return fetchVaultWinnerLogsAllPages(maxPages);
}

function normalizeAddress(address) {
  return address ? address.toLowerCase() : '';
}

function gameIdFromDecodedRow(row) {
  if (row.gameId) return row.gameId;
  const g = Object.values(GAMES).find((x) => x.shortName === row.gameName);
  return g?.id ?? 'strike';
}

const PLAYER_CHAIN_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;
const PLAYER_DATE_RANGE_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;
const playerChainHistoryInflight = new Map();
const playerHistoryByDateRangeCache = new Map();

function playerChainHistoryCacheKey(normalized) {
  return `player_chain_history_${normalized}`;
}

function getCachedPlayerChainHistory(normalized) {
  const cached = storage.get(playerChainHistoryCacheKey(normalized));
  if (!cached?.fetchedAt) return null;
  if (Date.now() - cached.fetchedAt > PLAYER_CHAIN_HISTORY_CACHE_TTL_MS) return null;
  return cached;
}

function setCachedPlayerChainHistory(normalized, payload) {
  storage.set(playerChainHistoryCacheKey(normalized), {
    ...payload,
    fetchedAt: Date.now(),
  });
}

function playerChainHistoryInflightKey(normalized, dateRange) {
  if (!hasDateRangeFilter(dateRange)) return normalized;
  return dateRangeHistoryCacheKey(normalized, dateRange);
}

function dateRangeHistoryCacheKey(normalized, filters) {
  return `${normalized}|${filters.dateFrom ?? ''}|${filters.dateTo ?? ''}|${filters.fromTimestampSec ?? ''}|${filters.toTimestampSec ?? ''}`;
}

function getPlayerHistoryDateRangeCached(cacheKey) {
  const entry = playerHistoryByDateRangeCache.get(cacheKey);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > PLAYER_DATE_RANGE_HISTORY_CACHE_TTL_MS) {
    playerHistoryByDateRangeCache.delete(cacheKey);
    return null;
  }
  return entry.payload;
}

function setPlayerHistoryDateRangeCached(cacheKey, payload) {
  playerHistoryByDateRangeCache.set(cacheKey, { payload, fetchedAt: Date.now() });
}

function filterBackendRecordsByDateRange(records, dateRange) {
  if (!hasDateRangeFilter(dateRange)) return records;
  const fromMs = parseFilterTimestampFrom(dateRange);
  const toMs = parseFilterTimestampTo(dateRange);
  if (fromMs == null && toMs == null) return records;

  return records.filter((record) => {
    const ts = Number(record?.timeStamp || 0) * 1000;
    if (fromMs != null && ts < fromMs) return false;
    if (toMs != null && ts > toMs) return false;
    return true;
  });
}

function mapBoughtRecordToChainRow(record, normalized) {
  const gameType = typeof record?.gameType === 'string' ? record.gameType.toLowerCase() : '';
  const price = Number(record?.price) || 0;
  let priceWeiStr = '0';
  try {
    priceWeiStr = ethers.utils.parseUnits(String(price), 18).toString();
  } catch {
    priceWeiStr = '0';
  }

  return {
    key: record?._id || `${record?.transactionHash || 'bought'}-${record?.number ?? ''}`,
    gameName: GAMES[gameType]?.shortName || gameType || 'Unknown',
    gameId: gameType || null,
    roundId: record?.roundId != null ? String(record.roundId) : '',
    userAddress: normalized,
    totalDepositedUsdt: price,
    priceWeiStr,
    pickedNumberStr: record?.number != null ? String(record.number) : '0',
    timestampMs: Number(record?.timeStamp || 0) * 1000,
    txHash: record?.transactionHash || null,
    sortKey: Number(record?.timeStamp || 0) * 1000,
  };
}

function mapWinnerRecordToChainWin(record, normalized) {
  const gameType = typeof record?.gameType === 'string' ? record.gameType.toLowerCase() : '';
  let prize = Number(record?.winnerAmount) || 0;
  if (prize > 1) {
    prize = prize * 0.9;
  }

  return {
    id: record?._id || `win-${record?.transactionHash || 'winner'}-${record?.winNumber ?? ''}`,
    timestamp: Number(record?.timeStamp || 0) * 1000,
    gameId: gameType || 'strike',
    numbers: [],
    pickedNumberStr: record?.winNumber != null ? String(record.winNumber) : undefined,
    onChain: true,
    walletAddress: normalized,
    cost: 0,
    result: prize > 1 ? 'won_direct' : 'won_neighbor',
    status: prize > 1 ? 'won_direct' : 'won_neighbor',
    prize,
    txHash: record?.lastBoughtTransaction || record?.transactionHash || null,
    roundId: record?.roundId != null ? String(record.roundId) : undefined,
  };
}

function chainRowsToBets(chainRows, normalized) {
  const mergedRows = mergeBoughtRowsByTxHash(chainRows);
  const chainBets = [];
  for (const row of mergedRows) {
    const pickedNumberStrs =
      Array.isArray(row.pickedNumberStrs) && row.pickedNumberStrs.length > 0
        ? row.pickedNumberStrs
        : row.pickedNumberStr != null && String(row.pickedNumberStr) !== ''
          ? [String(row.pickedNumberStr)]
          : [];
    chainBets.push({
      id: row.txHash || row.key,
      timestamp: row.timestampMs || 0,
      gameId: gameIdFromDecodedRow(row),
      numbers: [],
      pickedNumberStr: pickedNumberStrs[0] ?? '0',
      pickedNumberStrs,
      onChain: true,
      walletAddress: normalized,
      cost: row.totalDepositedUsdt,
      result: 'purchased',
      status: 'purchased',
      prize: 0,
      txHash: row.txHash,
      roundId: row.roundId,
    });
  }
  return chainBets;
}

function computePlayerStatsFromChain(chainRows, chainWins, normalized) {
  let spentWei = BigNumber.from(0);
  for (const row of chainRows) {
    spentWei = spentWei.add(BigNumber.from(row.priceWeiStr || '0'));
  }

  let chainSpentUsdt = 0;
  try {
    chainSpentUsdt = parseFloat(ethers.utils.formatUnits(spentWei, 18));
  } catch {
    chainSpentUsdt = 0;
  }

  const chainWonUsdt = chainWins.reduce((sum, w) => sum + (Number(w.prize) || 0), 0);
  const onChainBetCount = chainRows.length;
  const onChainWinCount = chainWins.length;

  const stats = storage.get(`player_stats_${normalized}`, {
    totalBets: 0,
    totalSpent: 0,
    totalWon: 0,
    totalWins: 0,
    totalLosses: 0,
    currentStreak: 0,
    bestStreak: 0,
  });

  const totalBets = onChainBetCount + (stats.totalBets || 0);
  const totalSpent = chainSpentUsdt + (Number(stats.totalSpent) || 0);
  const totalWon = chainWonUsdt + (Number(stats.totalWon) || 0);
  const totalWins = onChainWinCount + (Number(stats.totalWins) || 0);
  const winRate = totalSpent > 0 ? (totalWon / totalSpent) * 100 : 0;
  const netProfit = totalWon - totalSpent;

  return {
    ...stats,
    totalBets,
    totalSpent: Math.round(totalSpent * 1e6) / 1e6,
    totalWon: Math.round(totalWon * 1e6) / 1e6,
    totalWins,
    winRate: Math.round(winRate * 100) / 100,
    netProfit: Math.round(netProfit * 100) / 100,
  };
}

function countPurchasePickedNumbers(bet) {
  const list =
    Array.isArray(bet?.pickedNumberStrs) && bet.pickedNumberStrs.length > 0
      ? bet.pickedNumberStrs.filter((x) => x != null && String(x) !== '')
      : bet?.pickedNumberStr != null && String(bet.pickedNumberStr) !== ''
        ? [String(bet.pickedNumberStr)]
        : Array.isArray(bet?.numbers) && bet.numbers.length > 0
          ? bet.numbers.map(String)
          : [];
  return list.length;
}

function computeStatsFromFilteredBets(bets) {
  const purchases = bets.filter((b) => (Number(b.cost) || 0) > 0);
  const wins = bets.filter((b) => b.result && b.result.startsWith('won'));
  const totalBets = purchases.reduce((sum, b) => sum + countPurchasePickedNumbers(b), 0);
  const totalSpent = purchases.reduce((sum, b) => sum + (Number(b.cost) || 0), 0);
  const totalWon = wins.reduce((sum, b) => sum + (Number(b.prize) || 0), 0);
  const winRate = totalSpent > 0 ? (totalWon / totalSpent) * 100 : 0;

  return {
    totalBets,
    totalSpent: Math.round(totalSpent * 1e6) / 1e6,
    totalWon: Math.round(totalWon * 1e6) / 1e6,
    totalWins: wins.length,
    winRate: Math.round(winRate * 100) / 100,
    netProfit: Math.round((totalWon - totalSpent) * 100) / 100,
  };
}

function hasActiveHistoryFilters(filters = {}) {
  return Boolean(filters.gameId || filters.result || hasDateRangeFilter(filters));
}

async function buildPlayerChainHistory(normalized, dateRange = null) {
  const hasDateFilter = hasDateRangeFilter(dateRange);
  const inflightKey = playerChainHistoryInflightKey(normalized, dateRange);

  if (!hasDateFilter) {
    const cached = getCachedPlayerChainHistory(normalized);
    if (cached) return cached;
  }

  if (playerChainHistoryInflight.has(inflightKey)) {
    return playerChainHistoryInflight.get(inflightKey);
  }

  const task = (async () => {
    const chainRows = [];
    let chainBets = [];
    let chainWins = [];

    try {
      const [allBought, allWinners] = await Promise.all([
        fetchBoughtDataFromBackend(),
        fetchWinnerDataFromBackend(),
      ]);

      const boughtForWallet = filterBackendRecordsByDateRange(
        allBought.filter(
          (record) =>
            typeof record?.buyer === 'string' && record.buyer.toLowerCase() === normalized,
        ),
        dateRange,
      );
      const winnersForWallet = filterBackendRecordsByDateRange(
        allWinners.filter(
          (record) =>
            typeof record?.winnerAddress === 'string' &&
            record.winnerAddress.toLowerCase() === normalized,
        ),
        dateRange,
      );

      for (const record of boughtForWallet) {
        chainRows.push(mapBoughtRecordToChainRow(record, normalized));
      }
      chainBets = chainRowsToBets(chainRows, normalized);
      chainWins = winnersForWallet.map((record) => mapWinnerRecordToChainWin(record, normalized));
    } catch (e) {
      console.error('buildPlayerChainHistory failed:', e);
    }

    const stats = computePlayerStatsFromChain(chainRows, chainWins, normalized);
    const payload = { chainBets, chainWins, stats };
    if (!hasDateFilter && (chainBets.length > 0 || chainWins.length > 0)) {
      setCachedPlayerChainHistory(normalized, payload);
    }
    return payload;
  })().finally(() => {
    playerChainHistoryInflight.delete(inflightKey);
  });

  playerChainHistoryInflight.set(inflightKey, task);
  return task;
}

function applyBetFilters(allBets, filters) {
  let bets = [...allBets];

  if (filters.gameId) {
    bets = bets.filter((b) => b.gameId === filters.gameId);
  }
  if (filters.result) {
    if (filters.result === 'won') {
      bets = bets.filter((b) => b.result && b.result.startsWith('won'));
    } else if (filters.result === 'lost') {
      bets = bets.filter((b) => b.result === 'lost');
    } else if (filters.result === 'pending') {
      bets = bets.filter((b) => b.status === 'pending');
    }
  }
  const fromTs = parseFilterTimestampFrom(filters);
  if (fromTs != null) {
    bets = bets.filter((b) => b.timestamp >= fromTs);
  }
  const toTs = parseFilterTimestampTo(filters);
  if (toTs != null) {
    bets = bets.filter((b) => b.timestamp <= toTs);
  }

  bets.sort((a, b) => b.timestamp - a.timestamp);
  return bets;
}

function filterAndPaginateBets(allBets, page, limit, filters) {
  const bets = applyBetFilters(allBets, filters);
  return paginateHistoryBets(bets, page, limit);
}

/** Apply History table filters (game, result, date) without a network call. */
export function filterHistoryBets(allBets, filters = {}) {
  return applyBetFilters(allBets, filters);
}

/** Stats for a pre-filtered bet list (History game / date filters). */
export function computeHistoryStats(bets) {
  return computeStatsFromFilteredBets(bets);
}

/** Slice pre-filtered bet rows for History pagination (no network). */
export function paginateHistoryBets(allBets, page, limit) {
  const total = allBets.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * limit;

  return {
    bets: allBets.slice(start, start + limit),
    total,
    totalPages,
    page: safePage,
    limit,
  };
}

/** Return cached history page synchronously (instant pagination / filters). */
export function getCachedPlayerHistoryPage(walletAddress, page = 1, limit = 10, filters = {}) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return null;
  const cached = getCachedPlayerChainHistory(normalized);
  if (!cached) return null;
  const allBets = [...cached.chainWins, ...cached.chainBets];
  const history = filterAndPaginateBets(allBets, page, limit, filters);
  return { stats: cached.stats, history };
}

/** Return cached History rows for a wallet + date range (includes empty results). */
export function getCachedPlayerHistoryAllBets(walletAddress, filters = {}) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return null;

  let payload = null;
  if (hasDateRangeFilter(filters)) {
    payload = getPlayerHistoryDateRangeCached(dateRangeHistoryCacheKey(normalized, filters));
  } else {
    payload = getCachedPlayerChainHistory(normalized);
  }
  if (!payload) return null;

  const allBets = applyBetFilters([...payload.chainWins, ...payload.chainBets], filters);
  const stats = hasActiveHistoryFilters(filters)
    ? computeStatsFromFilteredBets(allBets)
    : payload.stats;
  return { stats, allBets };
}

/** Load stats + bet history with one shared backend fetch per wallet. */
export async function getPlayerHistoryBundle(walletAddress, page = 1, limit = 10, filters = {}) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) {
    return {
      stats: null,
      history: { bets: [], total: 0, totalPages: 1, page, limit },
    };
  }

  const hasDateFilter = hasDateRangeFilter(filters);
  const dateCacheKey = hasDateFilter ? dateRangeHistoryCacheKey(normalized, filters) : null;
  const dateRangeCached = dateCacheKey ? getPlayerHistoryDateRangeCached(dateCacheKey) : null;

  let chainBets;
  let chainWins;
  let chainStats;

  if (dateRangeCached) {
    ({ chainBets, chainWins, stats: chainStats } = dateRangeCached);
  } else {
    ({ chainBets, chainWins, stats: chainStats } = await buildPlayerChainHistory(
      normalized,
      hasDateFilter ? filters : null,
    ));

    if (dateCacheKey) {
      setPlayerHistoryDateRangeCached(dateCacheKey, { chainBets, chainWins, stats: chainStats });
    }
  }

  const allBets = [...chainWins, ...chainBets];
  const history = filterAndPaginateBets(allBets, page, limit, filters);
  const stats = hasActiveHistoryFilters(filters)
    ? computeStatsFromFilteredBets(applyBetFilters(allBets, filters))
    : chainStats;
  return { stats, history };
}

/** Clears per-wallet history cache (e.g. after a new bet). */
export function invalidatePlayerHistoryCache(walletAddress) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return;
  storage.remove(playerChainHistoryCacheKey(normalized));
  for (const key of playerChainHistoryInflight.keys()) {
    if (key === normalized || key.startsWith(`${normalized}|`)) {
      playerChainHistoryInflight.delete(key);
    }
  }
  for (const key of playerHistoryByDateRangeCache.keys()) {
    if (key.startsWith(`${normalized}|`)) {
      playerHistoryByDateRangeCache.delete(key);
    }
  }
}

// Register or get player
export async function getOrRegisterPlayer(walletAddress) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return null;

  const players = storage.get('players', {});

  if (!players[normalized]) {
    players[normalized] = {
      walletAddress: normalized,
      registeredAt: Date.now(),
      displayName: `${normalized.slice(0, 6)}...${normalized.slice(-4)}`,
      avatar: null,
    };
    storage.set('players', players);

    // Initialize stats
    storage.set(`player_stats_${normalized}`, {
      totalBets: 0,
      totalSpent: 0,
      totalWon: 0,
      totalWins: 0,
      totalLosses: 0,
      currentStreak: 0,
      bestStreak: 0,
    });
  }

  return players[normalized];
}

// Get player stats (on-chain Bought / Winner + localStorage demo fields)
export async function getPlayerStats(walletAddress) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return null;
  const { stats } = await buildPlayerChainHistory(normalized);
  return stats;
}

// Get bet history with pagination and filters
export async function getBetHistory(walletAddress, page = 1, limit = 10, filters = {}) {
  const { history } = await getPlayerHistoryBundle(walletAddress, page, limit, filters);
  return history;
}

// Get win history (on-chain Winner logs + local winning bets)
export async function getWinHistory(walletAddress, page = 1, limit = 10) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return { bets: [], total: 0, totalPages: 1, page, limit };

  let chainWins = [];
  try {
    const { chainWins: wins } = await buildPlayerChainHistory(normalized);
    chainWins = wins;
  } catch (e) {
    console.error(e);
  }

  const localWins = storage.getList('bets').filter(
    (b) =>
      normalizeAddress(b.walletAddress) === normalized &&
      b.result &&
      b.result.startsWith('won')
  );

  let wins = [...chainWins, ...localWins];
  wins.sort((a, b) => b.timestamp - a.timestamp);

  const total = wins.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const start = (page - 1) * limit;
  const paginatedBets = wins.slice(start, start + limit);

  return {
    bets: paginatedBets,
    total,
    totalPages,
    page,
    limit,
  };
}

// Get favorite numbers (most frequently played)
export async function getFavoriteNumbers(walletAddress, gameId = null) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return [];

  const bets = storage.getList('bets').filter(
    (b) => b.walletAddress === normalized && (!gameId || b.gameId === gameId)
  );

  const frequency = {};
  bets.forEach((bet) => {
    bet.numbers.forEach((num) => {
      const key = `${bet.gameId}_${num}`;
      frequency[key] = (frequency[key] || 0) + 1;
    });
  });

  return Object.entries(frequency)
    .map(([key, count]) => {
      const [gId, num] = key.split('_');
      return { gameId: gId, number: parseInt(num, 10), count };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

// Save AutoDraw settings
export async function saveAutoDrawSettings(walletAddress, settings) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return false;

  const validSettings = {
    enabled: Boolean(settings.enabled),
    gameId: settings.gameId || 'strike',
    numberCount: Math.min(Math.max(settings.numberCount || 1, 1), 10),
    frequency: settings.frequency || 'every_draw', // every_draw, daily, weekly
    maxEntries: settings.maxEntries || 10,
    stopOnWin: Boolean(settings.stopOnWin),
    minBalance: settings.minBalance || 0,
    updatedAt: Date.now(),
  };

  storage.set(`autodraw_${normalized}`, validSettings);
  return true;
}

// Get AutoDraw settings
export async function getAutoDrawSettings(walletAddress) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return null;

  return storage.get(`autodraw_${normalized}`, {
    enabled: false,
    gameId: 'strike',
    numberCount: 1,
    frequency: 'every_draw',
    maxEntries: 10,
    stopOnWin: false,
    minBalance: 0,
  });
}

// Get active AutoDraw entries count
export async function getActiveAutoDrawCount(walletAddress) {
  const normalized = normalizeAddress(walletAddress);
  if (!normalized) return 0;

  const settings = await getAutoDrawSettings(normalized);
  return settings.enabled ? settings.maxEntries : 0;
}

const playerApi = {
  getOrRegisterPlayer,
  getPlayerStats,
  getBetHistory,
  getWinHistory,
  getPlayerHistoryBundle,
  getCachedPlayerHistoryPage,
  getCachedPlayerHistoryAllBets,
  paginateHistoryBets,
  filterHistoryBets,
  computeHistoryStats,
  invalidatePlayerHistoryCache,
  getFavoriteNumbers,
  saveAutoDrawSettings,
  getAutoDrawSettings,
  getActiveAutoDrawCount,
  fetchWinNearLogsAllPages,
  decodeVaultWinnerLog,
  fetchVaultWinnerPayoutsFromTxHash,
};

export default playerApi;
